import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import { Tooltip } from '@mui/material';
import { palette } from '../../';

interface StepSliderProps {
  role: 'owner' | 'moderator' | 'creator' | 'player' | 'custom_creator' | string;
  column: number;
  initialValue: number;
  hideCreate: boolean;
  onPermissionChange: (column: number, value: number | null) => void;
  isEditActive: boolean;
}

const StepSlider: React.FC<StepSliderProps> = ({
  role,
  column,
  initialValue,
  hideCreate,
  onPermissionChange,
  isEditActive,
}) => {
  const [sliderValue, setSliderValue] = useState<number>(initialValue);
  const [disabled, setDisabled] = useState<boolean>(true);

  useEffect(() => {
    if (role === 'owner' || role === 'moderator') {
      setSliderValue(100); // Full privileges (Create)
      setDisabled(true); // Disable slider for owner and moderator
    } else if (role === 'creator') {
      if (column === 1) {
        setSliderValue(25); // None for first column (dbconnection)
      } else {
        setSliderValue(100); // Create for other columns
      }
      setDisabled(true); // Disable slider for standard creator role
    } else if (role === 'player') {
      if (column === 5) {
        setSliderValue(50); // View only
      } else {
        setSliderValue(25);
      }
      setDisabled(true); // Disable slider for player
    } else if (role === 'custom_creator') {
      if (column === 1) {
        setSliderValue(25);
        setDisabled(true);
      } else {
        setDisabled(false);
        if (column === 2) {
          setSliderValue(mapPrivilegeIdToSliderValue(initialValue));
        } else if (column === 3) {
          setSliderValue(mapPrivilegeIdToSliderValue(initialValue));
        } else if (column === 4) {
          setSliderValue(mapPrivilegeIdToSliderValue(initialValue));
        } else if (column === 5) {
          if (!initialValue) {
            setSliderValue(50);
          } else {
            setSliderValue(mapPrivilegeIdToSliderValue(initialValue));
          }
        }
      }
    } else {
      setSliderValue(25); // None (for any unhandled role)
      setDisabled(false); // Default to disabled slider
    }

    console.log('sliderValue', sliderValue);
  }, [
    role,
    column,
    initialValue,
  ]);

  const handleChange = (_event: Event, newValue: number | number[]): void => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    
    if (column === 5 && value < 50) {
      setSliderValue(50);
    } else {
      setSliderValue(value);
    }

    if (value > 25) onPermissionChange(column, mapSliderValueToPrivilegeId(value));
    if (value === 25) {
      onPermissionChange(column, 0);
    }
  };

  const mapPrivilegeIdToSliderValue = (privilegeId: number): number => {
    switch (privilegeId) {
      case 1:
        return 100; // Create
      case 2:
        return 75; // Edit
      case 3:
        return 50; // View
      case 0:
        return 25; // None
      default:
        return 25; // None
    }
  };

  const mapSliderValueToPrivilegeId = (value: number): number | null => {
    switch (value) {
      case 25:
        return null; // None
      case 50:
        return 3; // View
      case 75:
        return 2; // Edit
      case 100:
        return 1; // Create
      default:
        return null;
    }
  };

  return (
    <Tooltip title={disabled ? 'Disabled' : null} arrow
      slotProps={{
        tooltip: {
          sx: {
            fontSize: '12px',
          },
        },
      }}
    >
      <Box sx={{ width: '100%', color: 'black' }}>
        <Slider
          value={sliderValue}
          step={25}
          min={hideCreate ? 50 : 25}
          max={hideCreate ? 75 : 100}
          valueLabelFormat={(value: number): string => {
            switch (value) {
              case 25:
                if (column === 5) {
                  return 'None is not allowed in Playbook';
                } else {
                  return 'None';
                }
              case 50:
                return 'View';
              case 75:
                return 'Edit';
              case 100:
                return 'Create';
              default:
                return '';
            }
          }}
          aria-label="Slider"
          disabled={disabled || !isEditActive}
          onChange={handleChange}
          sx={
            !disabled && isEditActive
              ? {
                  width: hideCreate ? '79%' : '79%',
                  color: palette.secondary.light,
                  '& .MuiSlider-thumb': {
                    backgroundColor: palette.secondary.light,
                    border: `2px solid ${palette.secondary.light}`,
                    width: '17px',
                    height: '17px',
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: '0px 0px 0px 8px rgba(175, 153, 219, 0.16)',
                    },
                    '&.Mui-active': {
                      boxShadow: '0px 0px 0px 14px rgba(175, 153, 219, 0.16)',
                    },
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: palette.secondary.light,
                    background:
                      column === 5
                        ? `linear-gradient(to right, ${
                            palette.primary.grey
                          } 0%, ${palette.primary.grey} ${
                            2500 / (sliderValue - 25)
                          }%, ${palette.secondary.light} ${
                            2500 / (sliderValue - 25)
                          }%, ${palette.secondary.light} 100%)`
                        : null,
                    border: 'none',
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: palette.secondary.light,
                  },
                  '& .MuiSlider-thumb:hover': {
                    boxShadow: '0px 0px 0px 8px rgba(175, 153, 219, 0.16) !important',
                  },
                  '& .MuiSlider-thumb:active': {
                    boxShadow: '0px 0px 0px 8px rgba(175, 153, 219, 0.16) !important',
                  },
                  '& .MuiSlider-valueLabel': {
                    backgroundColor: palette.secondary.light,
                  },
                }
              : {
                  width: '79%',
                  '& .MuiSlider-thumb': {
                    backgroundColor: palette.primary.grey,
                    border: `2px solid ${palette.primary.grey}`,
                    width: '17px',
                    height: '17px',
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: '0px 0px 0px 8px rgba(175, 153, 219, 0.16)',
                    },
                    '&.Mui-active': {
                      boxShadow: '0px 0px 0px 14px rgba(175, 153, 219, 0.16)',
                    },
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: palette.primary.grey,
                    border: 'none',
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: palette.primary.grey,
                  },
                }
          }
        />
      </Box>
    </Tooltip>
  );
};

export default StepSlider;
